import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Calculate appropriate zoom level based on distance
function getZoomLevel(distanceKm) {
  if (distanceKm < 0.5) return 15;      // Less than 500m - street level
  if (distanceKm < 2) return 14;        // 500m - 2km
  if (distanceKm < 5) return 13;        // 2-5km
  if (distanceKm < 10) return 12;       // 5-10km
  if (distanceKm < 20) return 11;       // 10-20km
  if (distanceKm < 50) return 10;       // 20-50km
  return 9;                             // 50km+
}

export default function DeliveryMap({ pickup, delivery, route, height = 400 }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!pickup?.coordinates || !delivery?.coordinates) {
      console.log('Missing coordinates');
      return;
    }

    if (!mapRef.current) return;

    // Clean up existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const pickupLat = pickup.coordinates[1];
    const pickupLng = pickup.coordinates[0];
    const deliveryLat = delivery.coordinates[1];
    const deliveryLng = delivery.coordinates[0];

    // Calculate distance between points
    const distance = route?.distanceKm || 
      Math.sqrt(Math.pow(pickupLat - deliveryLat, 2) + Math.pow(pickupLng - deliveryLng, 2)) * 111;

    // Determine appropriate zoom level
    const zoomLevel = getZoomLevel(distance);

    // Create map with calculated zoom level
    const centerLat = (pickupLat + deliveryLat) / 2;
    const centerLng = (pickupLng + deliveryLng) / 2;
    const map = L.map(mapRef.current).setView([centerLat, centerLng], zoomLevel);
    
    // Use CartoDB tile server
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; CartoDB',
      subdomains: 'abcd',
      maxZoom: 19
    }).addTo(map);

    // Add markers
    L.marker([pickupLat, pickupLng])
      .bindPopup(`<strong>Pickup</strong><br/>${pickup.name || 'Vendor'}`)
      .addTo(map);

    L.marker([deliveryLat, deliveryLng])
      .bindPopup('<strong>Delivery</strong><br/>Your address')
      .addTo(map);

    // Add route line if available
    if (route?.path && route.path.length > 1) {
      const latLngs = route.path.map(coord => [coord[1], coord[0]]);
      L.polyline(latLngs, { color: '#16a34a', weight: 4, opacity: 0.8 }).addTo(map);
    }

    // If points are very close, add a small offset to see both markers
    if (distance < 0.5) {
      // Add a circle to show the area
      L.circle([pickupLat, pickupLng], {
        radius: 200,
        color: '#16a34a',
        fillColor: '#22c55e',
        fillOpacity: 0.1
      }).addTo(map);
    }

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [pickup, delivery, route]);

  if (!pickup?.coordinates || !delivery?.coordinates) {
    return <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-500">Location data not available</div>;
  }

  return (
    <div className="rounded-lg overflow-hidden shadow-md">
      <div ref={mapRef} style={{ height: `${height}px`, width: '100%', backgroundColor: '#e5e5e5' }} />
      {route && route.distanceKm > 0 && (
        <div className="bg-gray-50 p-3 text-sm text-gray-600 border-t">
          <span className="font-medium">Distance:</span> {route.distanceKm} km • 
          <span className="font-medium ml-2">Est. delivery:</span> {route.durationMinutes} minutes
        </div>
      )}
      {route && route.distanceKm === 0 && (
        <div className="bg-gray-50 p-3 text-sm text-gray-600 border-t">
          <span className="font-medium">Pickup and delivery are at the same location</span>
        </div>
      )}
    </div>
  );
}
